import type {
  AhpSubmissionMetadata,
  DirectTurnTelemetryContext,
  DispatchReceipt,
  EstablishedAhpSession,
  EstablishedAhpSessionIntegrationServices,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {EstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import {
  withClientStopObserver,
  type ClientPreparationOptions,
} from '@github-ui/ahp-session-client/client-interaction-observer'
import {
  clientInteractionAccepted,
  clientInteractionForEvent,
  clientInteractionFailed,
  clientInteractionPending,
  createStopInteractionObserver,
  prepareClientInteraction,
  type ClientInteractionEvent,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEventHandler} from 'react'

import {getEstablishedAhpSessionIntegrations} from './use-established-ahp-session-integrations'
import {useOptionalEstablishedAhpSession} from './use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from './use-established-ahp-session-view'

type PendingAction = 'submit' | 'queue' | 'stop' | undefined

export type EstablishedAhpPromptDispatchKind = 'direct' | 'initial' | 'queue' | 'steer'

interface UseEstablishedAhpSessionInputControllerOptions {
  value: string
  disabled: boolean
  readOnly: boolean
  draftOnly?: boolean
  preparePrompt?: (text: string, options?: ClientPreparationOptions) => PreparedPrompt | Promise<PreparedPrompt>
  preparePromptForDispatch?: (
    prompt: PreparedPrompt,
    kind: EstablishedAhpPromptDispatchKind,
  ) => PreparedPrompt | Promise<PreparedPrompt>
  directTurnTelemetryContext?: DirectTurnTelemetryContext
  directTurnModelId?: string
  beforeSubmit?: () => Promise<void> | void
  beforeQueue?: () => Promise<void> | void
  beforeDispatch?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<void> | void
  configureSession?: (session: EstablishedAhpSession, prompt?: PreparedPrompt) => Promise<void>
  /**
   * Applies a prompt that configures the session rather than addressing the
   * model. A `true` result means the prompt was handled here, so it is accepted
   * (clearing the composer) without ever being dispatched as a turn.
   */
  consumePrompt?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<boolean>
  /**
   * Applies input text that needs no session, such as a client-side command. It
   * receives the raw input before `preparePrompt` or any other submission work,
   * so preparation can neither rewrite the command nor run for it. A `true`
   * result is accepted like a submission (`onSubmitAccepted`, with no receipt)
   * without a prompt being prepared, a session established, or a turn dispatched.
   */
  consumeSessionlessPrompt?: (text: string) => Promise<boolean>
  onSubmitAccepted?: (receipt?: DispatchReceipt) => void
  onQueueAccepted?: (receipt: DispatchReceipt) => void
  onDispatchAccepted?: (
    prompt: PreparedPrompt,
    kind: EstablishedAhpPromptDispatchKind,
    receipt?: DispatchReceipt,
  ) => void
  onPromptReleased?: (prompt: PreparedPrompt) => void
  onStopped?: () => void
  onError?: (error: unknown) => void
  sessionIntegrationServices: EstablishedAhpSessionIntegrationServices
  initialSubmission?: EstablishedAhpInitialSubmissionCoordinator
  initialSubmissionOrigin: 'new' | 'recovery'
  initialSubmissionMetadata?: AhpSubmissionMetadata
  initialSubmissionPending: boolean
  getEstablishedSession?: () => EstablishedAhpSession | undefined
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  onInitialSubmissionStarted?: (prompt: PreparedPrompt) => void
  onInitialSubmissionSettled?: () => void
}

function defaultPrompt(text: string): PreparedPrompt {
  return {text}
}

function contextForSession(
  context: DirectTurnTelemetryContext,
  session: EstablishedAhpSession,
): DirectTurnTelemetryContext {
  return {
    ...context,
    environmentId: session.identity.environmentId,
    taskId: session.identity.taskId ?? context.taskId,
    sessionId: session.identity.sessionId,
  }
}

function clearEarlyTelemetry(
  telemetry: EstablishedAhpSession['directTurnTelemetry'],
  requestId: string | undefined,
): void {
  if (requestId && telemetry) telemetry.preDispatchError(requestId)
}

function createInitialSubmissionHandoff(_initialSubmission: EstablishedAhpInitialSubmissionCoordinator | undefined) {
  let establishedSession: EstablishedAhpSession | undefined
  let current: symbol | undefined
  return {
    getSession: () => establishedSession,
    setSession: (session: EstablishedAhpSession | undefined) => {
      establishedSession = session
    },
    start: () => {
      current = Symbol()
      return current
    },
    isCurrent: (action: symbol) => current === action,
    clear: (action: symbol) => {
      if (current !== action) return false
      current = undefined
      return true
    },
    release: () => {
      current = undefined
    },
  }
}

export function useEstablishedAhpSessionInputController({
  value,
  disabled,
  readOnly,
  draftOnly = false,
  preparePrompt = defaultPrompt,
  preparePromptForDispatch,
  directTurnTelemetryContext,
  directTurnModelId,
  beforeSubmit,
  beforeQueue,
  beforeDispatch,
  configureSession,
  consumePrompt,
  consumeSessionlessPrompt,
  onSubmitAccepted,
  onQueueAccepted,
  onDispatchAccepted,
  onStopped,
  onError,
  sessionIntegrationServices,
  initialSubmission,
  initialSubmissionOrigin,
  initialSubmissionMetadata,
  initialSubmissionPending,
  getEstablishedSession,
  onSessionEstablished,
  onInitialSubmissionStarted,
  onInitialSubmissionSettled,
}: UseEstablishedAhpSessionInputControllerOptions) {
  const session = useOptionalEstablishedAhpSession()
  const initialSubmissionHandoff = useMemo(() => createInitialSubmissionHandoff(initialSubmission), [initialSubmission])
  useEffect(() => {
    initialSubmissionHandoff.setSession(session)
  }, [initialSubmissionHandoff, session])
  const integrations = useMemo(
    () => (session ? {...getEstablishedAhpSessionIntegrations(session), ...sessionIntegrationServices} : undefined),
    [session, sessionIntegrationServices],
  )
  const view = useOptionalEstablishedAhpSessionViewState()
  const [pendingAction, setPendingAction] = useState<PendingAction>()
  const [pendingRecoverySubmission, setPendingRecoverySubmission] = useState(false)
  const [pendingRecoveryStopAccepted, setPendingRecoveryStopAccepted] = useState(false)

  if (pendingRecoveryStopAccepted && !pendingRecoverySubmission && !initialSubmissionPending) {
    setPendingRecoveryStopAccepted(false)
  }

  const isReadOnly = readOnly || view.passive
  const sessionUnavailable = !session && !initialSubmission
  const inputDisabled =
    disabled || pendingAction !== undefined || initialSubmissionPending || (sessionUnavailable && !draftOnly)
  const isDisabled = inputDisabled || isReadOnly
  const commandsDisabled = isDisabled || draftOnly || sessionUnavailable
  const commandCapabilityDisabled = initialSubmissionPending || isReadOnly || draftOnly || sessionUnavailable
  const commandCapabilityDisabledRef = useRef(commandCapabilityDisabled)
  useLayoutEffect(() => {
    commandCapabilityDisabledRef.current = commandCapabilityDisabled
  }, [commandCapabilityDisabled])
  const canQueue = session !== undefined && integrations !== undefined && !commandsDisabled
  const hasActiveTurn = view.snapshot.state?.activeTurn !== undefined
  const activeTurnId = view.snapshot.state?.activeTurn?.id
  const hasPendingRecoveryStop =
    (pendingRecoverySubmission || initialSubmissionPending) &&
    !pendingRecoveryStopAccepted &&
    initialSubmissionOrigin === 'recovery' &&
    initialSubmission?.cancel !== undefined
  const canStop =
    !isReadOnly &&
    !draftOnly &&
    pendingAction !== 'stop' &&
    ((hasActiveTurn && integrations !== undefined && !disabled) || hasPendingRecoveryStop)

  const runSubmission = useCallback(
    async (action: 'submit' | 'queue', event?: ClientInteractionEvent) => {
      const interaction = clientInteractionForEvent(event)
      if (commandsDisabled || (action === 'queue' && !session)) {
        interaction?.failed('handler_rejected')
        return
      }
      interaction?.preparationStarted()
      const pendingActionToken = initialSubmissionHandoff.start()
      setPendingAction(action)
      clientInteractionPending(interaction)
      const canDispatch = () =>
        initialSubmissionHandoff.isCurrent(pendingActionToken) && !commandCapabilityDisabledRef.current
      let ownsPendingAction = false
      let earlySession: EstablishedAhpSession | undefined
      let earlyTelemetry: EstablishedAhpSession['directTurnTelemetry']
      let directTurnRequestId: string | undefined
      let directSubmissionAttempted = false
      let dispatchedPrompt: PreparedPrompt
      let dispatchKind: EstablishedAhpPromptDispatchKind
      try {
        if (consumeSessionlessPrompt && (await consumeSessionlessPrompt(value))) {
          if (canDispatch()) onSubmitAccepted?.()
          return
        }
        const candidateSession = getEstablishedSession?.() ?? initialSubmissionHandoff.getSession() ?? session
        const acceptsDirectTurn =
          action === 'submit' && directTurnTelemetryContext !== undefined && candidateSession !== undefined
        if (acceptsDirectTurn) {
          earlySession = candidateSession
          earlyTelemetry = candidateSession.directTurnTelemetry
          directTurnRequestId = crypto.randomUUID()
          earlyTelemetry?.beginSubmission({
            requestId: directTurnRequestId,
            turnKind: 'existing_chat',
            startPath: 'active',
            ...(directTurnModelId ? {modelId: directTurnModelId} : {}),
            context: contextForSession(directTurnTelemetryContext, candidateSession),
          })
        }
        const prompt = await (interaction ? preparePrompt(value, {interaction}) : preparePrompt(value))
        if (!canDispatch()) {
          interaction?.unresolved('cancelled')
          return
        }
        if (!prompt.text.trim() && !(prompt.attachments && prompt.attachments.length > 0)) {
          interaction?.failed('payload_mismatch')
          return
        }
        prepareClientInteraction(interaction, prompt)
        interaction?.dependencyChanged('configuration', true)
        if (action === 'submit') await beforeSubmit?.()
        else await beforeQueue?.()
        if (!canDispatch()) {
          interaction?.unresolved('cancelled')
          return
        }
        let receipt: DispatchReceipt | undefined
        const activeSession = getEstablishedSession?.() ?? initialSubmissionHandoff.getSession() ?? session
        if (activeSession) {
          await configureSession?.(activeSession, prompt)
          if (!canDispatch()) {
            interaction?.unresolved('cancelled')
            return
          }
          if (await consumePrompt?.(activeSession, prompt)) {
            interaction?.unresolved('handoff_unobserved')
            if (!canDispatch()) return
            // The prompt configured the session, so there is no receipt and
            // nothing to dispatch. It is still accepted so the composer clears.
            if (initialSubmissionHandoff.isCurrent(pendingActionToken)) onSubmitAccepted?.()
            return
          }
          if (!canDispatch()) {
            interaction?.unresolved('cancelled')
            return
          }
        }
        interaction?.dependencyChanged('configuration', false)
        const activeIntegrations = activeSession
          ? {...getEstablishedAhpSessionIntegrations(activeSession), ...sessionIntegrationServices}
          : integrations
        if (activeSession && activeIntegrations) {
          const activeTurn = activeSession.view.getSnapshot().state?.activeTurn !== undefined
          dispatchKind = action === 'queue' ? 'queue' : activeTurn ? 'steer' : 'direct'
          const promptForDispatch = (await preparePromptForDispatch?.(prompt, dispatchKind)) ?? prompt
          dispatchedPrompt = promptForDispatch
          const prepareDispatch = async () => {
            if (!canDispatch()) throw new DOMException('The submission is no longer available.', 'AbortError')
            await beforeDispatch?.(activeSession, promptForDispatch)
            if (!canDispatch()) throw new DOMException('The submission is no longer available.', 'AbortError')
          }
          const submissionContext =
            action === 'submit' && directTurnTelemetryContext
              ? contextForSession(directTurnTelemetryContext, activeSession)
              : undefined
          if (submissionContext) {
            directTurnRequestId = directTurnRequestId ?? crypto.randomUUID()
            const activeTelemetry = activeSession.directTurnTelemetry
            if (earlySession !== activeSession) {
              if (earlyTelemetry === activeTelemetry) {
                earlyTelemetry?.updateSubmissionAttribution(directTurnRequestId, submissionContext, directTurnModelId)
              } else {
                clearEarlyTelemetry(earlyTelemetry, directTurnRequestId)
                earlyTelemetry = activeTelemetry
                earlyTelemetry?.beginSubmission({
                  requestId: directTurnRequestId,
                  turnKind: 'existing_chat',
                  startPath: 'active',
                  ...(directTurnModelId ? {modelId: directTurnModelId} : {}),
                  context: submissionContext,
                })
              }
            }
          }
          if (action === 'submit') {
            if (activeTurn) {
              receipt = await activeIntegrations.input.steer(
                promptForDispatch,
                undefined,
                prepareDispatch,
                directTurnRequestId,
              )
            } else {
              directSubmissionAttempted = true
              if (submissionContext) {
                receipt = await activeIntegrations.input.submit(
                  promptForDispatch,
                  directTurnRequestId,
                  prepareDispatch,
                  submissionContext,
                )
              } else {
                receipt = await activeIntegrations.input.submit(promptForDispatch, undefined, prepareDispatch)
              }
            }
          } else {
            receipt = await activeIntegrations.input.queue(promptForDispatch, prepareDispatch)
          }
        } else if (action === 'submit' && initialSubmission) {
          interaction?.dependencyChanged('session', true)
          dispatchKind = 'initial'
          const promptForDispatch = (await preparePromptForDispatch?.(prompt, dispatchKind)) ?? prompt
          dispatchedPrompt = promptForDispatch
          onInitialSubmissionStarted?.(promptForDispatch)
          if (initialSubmissionOrigin === 'recovery' && initialSubmission.cancel) setPendingRecoverySubmission(true)
          const establishedSession = await initialSubmission.submit({
            prompt: promptForDispatch,
            origin: initialSubmissionOrigin,
            ...(initialSubmissionMetadata ? {metadata: initialSubmissionMetadata} : {}),
            ...(configureSession ? {configureSession} : {}),
            ...(directTurnTelemetryContext ? {directTurnTelemetryContext} : {}),
          })
          interaction?.dependencyChanged('session', false)
          initialSubmissionHandoff.setSession(establishedSession)
          onSessionEstablished?.(establishedSession)
          // The first prompt is what establishes the session, so a configuring
          // prompt cannot be consumed before there is a session to apply it to.
          await consumePrompt?.(establishedSession, promptForDispatch)
        } else {
          throw new Error('An established AHP session is required to submit this action.')
        }
        if (initialSubmissionHandoff.isCurrent(pendingActionToken)) {
          clientInteractionAccepted(interaction)
          onDispatchAccepted?.(dispatchedPrompt, dispatchKind, receipt)
          if (action === 'submit') onSubmitAccepted?.(receipt)
          else if (receipt) onQueueAccepted?.(receipt)
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') interaction?.unresolved('cancelled')
        else clientInteractionFailed(interaction)
        if (initialSubmissionHandoff.isCurrent(pendingActionToken)) onError?.(error)
      } finally {
        if (!directSubmissionAttempted) clearEarlyTelemetry(earlyTelemetry, directTurnRequestId)
        ownsPendingAction = initialSubmissionHandoff.clear(pendingActionToken)
        if (ownsPendingAction) {
          if (action === 'submit') setPendingRecoverySubmission(false)
          setPendingAction(undefined)
        }
      }
      if (ownsPendingAction && action === 'submit' && initialSubmission && !session) onInitialSubmissionSettled?.()
    },
    [
      initialSubmission,
      initialSubmissionHandoff,
      initialSubmissionOrigin,
      initialSubmissionMetadata,
      integrations,
      commandsDisabled,
      getEstablishedSession,
      onError,
      onDispatchAccepted,
      onQueueAccepted,
      onSubmitAccepted,
      onInitialSubmissionSettled,
      onInitialSubmissionStarted,
      onSessionEstablished,
      beforeQueue,
      beforeDispatch,
      beforeSubmit,
      configureSession,
      consumePrompt,
      consumeSessionlessPrompt,
      directTurnModelId,
      directTurnTelemetryContext,
      preparePrompt,
      preparePromptForDispatch,
      sessionIntegrationServices,
      session,
      value,
    ],
  )

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    event => {
      event.preventDefault()
      void runSubmission('submit', event.nativeEvent)
    },
    [runSubmission],
  )
  const submit = useCallback((event?: ClientInteractionEvent) => void runSubmission('submit', event), [runSubmission])
  const queue = useCallback((event?: ClientInteractionEvent) => void runSubmission('queue', event), [runSubmission])

  const stop = useCallback(
    (event?: ClientInteractionEvent) => {
      const interaction = clientInteractionForEvent(event)
      if (isReadOnly || draftOnly) {
        interaction?.failed('handler_rejected')
        return
      }
      const stopObserver = createStopInteractionObserver(interaction, activeTurnId)
      if (hasPendingRecoveryStop) {
        try {
          const outcome = withClientStopObserver(stopObserver, () => initialSubmission?.cancel?.())
          if (outcome?.accepted) {
            clientInteractionAccepted(interaction)
            initialSubmissionHandoff.release()
            setPendingRecoverySubmission(false)
            setPendingRecoveryStopAccepted(true)
            setPendingAction(undefined)
            onStopped?.()
            return true
          }
          interaction?.unresolved('stop_target_unverified')
        } catch (error) {
          interaction?.failed('transport_failed')
          onError?.(error)
        }
        return
      }
      if (disabled || pendingAction === 'stop' || !activeTurnId || !integrations) {
        interaction?.unresolved('stop_target_unverified')
        return
      }
      const snapshot = session?.view.getSnapshot()
      if (snapshot?.passive || snapshot?.state?.activeTurn?.id !== activeTurnId) {
        interaction?.unresolved(
          snapshot?.state?.activeTurn?.id && snapshot.state.activeTurn.id !== activeTurnId
            ? 'stop_target_changed'
            : 'stop_target_unverified',
        )
        return
      }
      let stopped = false
      setPendingAction('stop')
      try {
        const outcome = withClientStopObserver(stopObserver, () => integrations.commands.cancel(activeTurnId))
        if (outcome.accepted) {
          clientInteractionAccepted(interaction)
          stopped = true
          initialSubmissionHandoff.release()
          onStopped?.()
        } else {
          interaction?.unresolved('stop_target_unverified')
        }
      } catch (error) {
        interaction?.failed('transport_failed')
        onError?.(error)
      } finally {
        setPendingAction(stopped ? undefined : pendingAction)
      }
      return stopped
    },
    [
      activeTurnId,
      disabled,
      hasPendingRecoveryStop,
      initialSubmission,
      initialSubmissionHandoff,
      integrations,
      pendingAction,
      session,
      draftOnly,
      isReadOnly,
      onError,
      onStopped,
    ],
  )

  return {
    pendingAction,
    inputDisabled,
    isDisabled,
    commandsDisabled,
    isReadOnly,
    canQueue,
    canStop,
    hasActiveTurn,
    hasPendingRecoveryStop,
    handleSubmit,
    submit,
    queue,
    stop,
  }
}
